import { OFFICE_365_TOKEN_PATH } from '@my-backoffice/app/pages/login/federated/office365/constants/office365-auth.constants';
import { IOffice365AuthToken } from '@my-backoffice/app/pages/login/federated/office365/interfaces/office365-auth-token.interface';
import { Office365AuthenticationService } from '@my-backoffice/app/pages/login/federated/office365/services/office365-authentication.service';
import { AI_API_ACCESS_TOKEN } from '@my-backoffice/app/shared/constants/ai-api-access-token.constants';
import { EStorageKeys } from '@my-backoffice/app/shared/models/enums/storage-keys.enum';
import { IHttpResponseBody } from '@my-backoffice/app/shared/models/interfaces/http-response-body.interface';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { CookieService } from 'ngx-cookie';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

@Injectable()
export class Office365HttpAuthInterceptor implements HttpInterceptor {
  private readonly _injector = inject(Injector);

  public constructor(private readonly _cookieService: CookieService) {}

  private get _authenticationService(): Office365AuthenticationService {
    return this._injector.get(Office365AuthenticationService);
  }

  public intercept<T>(request: Readonly<HttpRequest<T>>, next: Readonly<HttpHandler>): Observable<HttpEvent<T>> {
    const token = this._cookieService.get(EStorageKeys.AUTH_TOKEN);
    let userId = this._cookieService.get(EStorageKeys.USER_ID);
    const refreshTokenExpire = this._cookieService.get(EStorageKeys.REFRESH_TOKEN_EXPIRE);
    const authTokenExpire = this._cookieService.get(EStorageKeys.AUTH_TOKEN_EXPIRE);
    const shouldRefresh =
      refreshTokenExpire &&
      authTokenExpire &&
      token &&
      !request.url.includes(`${OFFICE_365_TOKEN_PATH}?refreshToken`) &&
      this._isTokenExpired(EStorageKeys.AUTH_TOKEN_EXPIRE) &&
      !this._isTokenExpired(EStorageKeys.REFRESH_TOKEN_EXPIRE);

    if (shouldRefresh) {
      return this._authenticationService.refreshAccessToken$().pipe(
        catchError((): Observable<IHttpResponseBody<IOffice365AuthToken>> => {
          this._authenticationService.navigateToLogin();

          return throwError((): HttpErrorResponse => this._unauthorizedHttpError());
        }),
        switchMap((response: IHttpResponseBody<IOffice365AuthToken>): Observable<HttpEvent<T>> => {
          if (!response?.content) {
            this._authenticationService.navigateToLogin();

            return throwError((): HttpErrorResponse => this._unauthorizedHttpError());
          }

          this._authenticationService.saveTokenAndExpiration(response.content);

          const newToken = this._cookieService.get(EStorageKeys.AUTH_TOKEN);

          userId = this._cookieService.get(EStorageKeys.USER_ID);

          if (newToken && userId) {
            return next.handle(this._cloneAuthorizedRequest(request, newToken, userId));
          }

          this._authenticationService.navigateToLogin();

          return throwError((): HttpErrorResponse => this._unauthorizedHttpError());
        })
      );
    }

    if (token && userId) {
      return next.handle(this._cloneAuthorizedRequest(request, token, userId));
    }

    this._authenticationService.navigateToLogin();

    return throwError((): HttpErrorResponse => this._unauthorizedHttpError());
  }

  private _unauthorizedHttpError(): HttpErrorResponse {
    return new HttpErrorResponse({ status: 401, statusText: 'Unauthorized' });
  }

  private _cloneAuthorizedRequest<T>(request: Readonly<HttpRequest<T>>, token: string, userId: string): HttpRequest<T> {
    return request.clone({
      setHeaders: {
        'authorization': `Bearer ${token}`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'ai-user-id': userId,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'ai-api-access-token': AI_API_ACCESS_TOKEN,
      },
    });
  }

  private _isTokenExpired(tokenKey: EStorageKeys): boolean {
    const tokenExpiredTime = this._cookieService.get(tokenKey) || '0';

    return Date.now() / 1000 >= +tokenExpiredTime;
  }
}
